import {
  CANVAS_MORPH_HERO_IMAGE_SELECTOR,
  CANVAS_SELECTED_MORPH_HERO_IMAGE_SELECTOR,
  fitCanvasToNodes,
  getLocalizationFitViewPadding,
  useEditCanvasStore,
} from '@air/feature-edit-canvas';

import { ASSET_MODAL_MORPH_HERO_IMAGE_SELECTOR } from '~/components/AssetModalV2/Visualizer/ImageVisualizer/shared/constants';
import { usePendingCanvasEditStore } from '~/components/v2/store/features/canvas/usePendingCanvasEditStore';

const HERO_TRANSITION_NAME = 'asset-modal-hero';
const IDENTITY_VIEWPORT = /^translate\(\s*0(?:px)?\s*,\s*0(?:px)?\s*\)\s*scale\(\s*1\s*\)$/;

const isIdentityViewportTransform = (transform: string) => !transform || IDENTITY_VIEWPORT.test(transform.trim());
const TARGET_TIMEOUT_MS = 600;
const POLL_MS = 16;

const findCanvasImage = () =>
  document.querySelector<HTMLImageElement>(CANVAS_SELECTED_MORPH_HERO_IMAGE_SELECTOR) ??
  document.querySelector<HTMLImageElement>(CANVAS_MORPH_HERO_IMAGE_SELECTOR);

const findAssetModalImage = () => document.querySelector<HTMLImageElement>(ASSET_MODAL_MORPH_HERO_IMAGE_SELECTOR);

// setTimeout, not rAF: no paints while the view-transition update callback is pending.
const waitFor = async <T>(find: () => T | null): Promise<T | null> => {
  const deadline = performance.now() + TARGET_TIMEOUT_MS;

  while (performance.now() < deadline) {
    const element = find();

    if (element) {
      return element;
    }

    await new Promise<void>((resolve) => setTimeout(resolve, POLL_MS));
  }

  return null;
};

type Hero = {
  element: HTMLElement;
  release: () => void;
};

const heroOf = (element: HTMLElement): Hero => ({
  element,
  release: () => element.style.removeProperty('view-transition-name'),
});

const isLoaded = (image: HTMLImageElement) => image.complete && image.naturalWidth > 0;

// A view transition interpolates layout size and transform separately, so a node under the
// viewport's scale (big layout box, small scale) balloons mid-glide. Glide a fixed copy at the
// node's screen rect instead, and hide the node until the glide ends. The copy is a <canvas>
// painted from the already-decoded node image: a fresh <img> is captured blank until it decodes,
// which never happens in time while the transition holds rendering.
const ghostCanvasNode = (image: HTMLImageElement, node: HTMLElement): Hero | null => {
  const { height, left, top, width } = node.getBoundingClientRect();

  if (!isLoaded(image) || width <= 0 || height <= 0) {
    return null;
  }

  const ghost = document.createElement('canvas');
  const scale = window.devicePixelRatio || 1;

  ghost.width = Math.round(width * scale);
  ghost.height = Math.round(height * scale);

  // object-fit: cover — crop the source to the ghost's aspect ratio, centered.
  const { naturalHeight, naturalWidth } = image;
  const cropWidth = Math.min(naturalWidth, (naturalHeight * width) / height);
  const cropHeight = Math.min(naturalHeight, (naturalWidth * height) / width);

  ghost
    .getContext('2d')
    ?.drawImage(
      image,
      (naturalWidth - cropWidth) / 2,
      (naturalHeight - cropHeight) / 2,
      cropWidth,
      cropHeight,
      0,
      0,
      ghost.width,
      ghost.height,
    );

  Object.assign(ghost.style, {
    height: `${height}px`,
    left: `${left}px`,
    pointerEvents: 'none',
    position: 'fixed',
    top: `${top}px`,
    transform: image.style.transform,
    width: `${width}px`,
    zIndex: '2147483647',
  });
  node.style.opacity = '0';
  document.body.append(ghost);

  return {
    element: ghost,
    release: () => {
      ghost.remove();
      node.style.removeProperty('opacity');
    },
  };
};

// The canvas fits its viewport in a rAF and React Flow keeps a node hidden until it measures it,
// and neither runs while a view transition holds paints. Do both by hand so the transition
// captures the canvas as it will look. React Flow is told too, or its next animated fit would
// start from the unfit viewport.
const findFittedCanvasNode = (isTranslate: boolean) => {
  const image = findCanvasImage();
  const node = image?.closest<HTMLElement>('.react-flow__node');
  const viewport = document.querySelector<HTMLElement>('.react-flow__viewport');

  // Keep polling until the node image has loaded: the ghost is painted from it.
  if (!image || !node || !viewport || !isLoaded(image)) {
    return null;
  }

  if (isIdentityViewportTransform(viewport.style.transform)) {
    // Translate frames the image beside its card, so land there instead of panning after the glide.
    const isFit = fitCanvasToNodes({
      maxZoom: isTranslate ? undefined : 1,
      nodes: useEditCanvasStore.getState().nodes,
      padding: isTranslate
        ? getLocalizationFitViewPadding(viewport.closest('.react-flow')?.clientWidth ?? 0)
        : undefined,
      setViewport: ({ x, y, zoom }) => {
        useEditCanvasStore.getState().applyViewport?.({ x, y, zoom });
        viewport.style.transform = `translate(${x}px, ${y}px) scale(${zoom})`;
      },
    });

    if (!isFit) {
      return null;
    }
  }

  node.style.visibility = 'visible';

  return ghostCanvasNode(image, node);
};

type MorphHeroParams = {
  findSource: () => Hero | null;
  findTarget: () => Hero | null;
  navigate: () => void;
};

const morphHero = ({ findSource, findTarget, navigate }: MorphHeroParams): void => {
  const source =
    !document.startViewTransition || window.matchMedia('(prefers-reduced-motion: reduce)').matches
      ? null
      : findSource();

  if (!source) {
    navigate();
    return;
  }

  source.element.style.viewTransitionName = HERO_TRANSITION_NAME;

  let target: Hero | null = null;
  const transition = document.startViewTransition(async () => {
    navigate();
    // The old state is already captured. A ghost on <body> outlives the route it came from, and
    // two elements sharing the name when the new state is captured would skip the transition.
    source.release();
    target = await waitFor(findTarget);

    if (target) {
      target.element.style.viewTransitionName = HERO_TRANSITION_NAME;
    }
  });

  transition.ready.catch(() => undefined);
  // The target outlives the glide, so it must not keep the name (or the ghost, on the canvas end).
  transition.finished.catch(() => undefined).then(() => target?.release());
};

export const morphAssetModalImageToCanvas = (navigate: () => void): void => {
  // Read now: the canvas consumes the pending edit as soon as it mounts.
  const isTranslate = usePendingCanvasEditStore.getState().pending?.action === 'translate';

  morphHero({
    findSource: () => {
      const image = findAssetModalImage();

      return image ? heroOf(image) : null;
    },
    findTarget: () => findFittedCanvasNode(isTranslate),
    navigate,
  });
};
